import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useActiveCustomerId, useCustomerScope } from '../contexts/AccountContext';

/**
 * Allowlist: never SELECT *. internal_notes is dropped at the query layer;
 * customers_customer_view is the defense-in-depth counterpart.
 */
const CUSTOMER_COLUMNS =
  'id, name, account_number, sla_tier, billing_cycle, mrr_cents, contract_renewal_date, status, created_at, updated_at';

export interface CustomerRecord {
  id: string;
  name: string;
  account_number: string;
  sla_tier: string | null;
  billing_cycle: string | null;
  mrr_cents: number | null;
  contract_renewal_date: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
}

/** One account's record. Defaults to the active account (null in portfolio mode). */
export function useCustomer(customerId?: string | null) {
  const active = useActiveCustomerId();
  const scope = useCustomerScope();
  const id = customerId === undefined ? active : customerId;
  const inScope = !!id && scope.ids.includes(id);

  return useQuery({
    queryKey: ['customer', scope.ownerKey, id],
    enabled: scope.ready && inScope,
    queryFn: async (): Promise<CustomerRecord | null> => {
      const { data, error } = await supabase
        .from('customers_customer_view')
        .select(CUSTOMER_COLUMNS)
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as CustomerRecord | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Every account in the current scope (one row in single mode). */
export function useCustomersInScope() {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customers-in-scope', scope.key],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<CustomerRecord[]> => {
      const { data, error } = await supabase
        .from('customers_customer_view')
        .select(CUSTOMER_COLUMNS)
        .in('id', scope.ids)
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as CustomerRecord[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
